
import React, {useMemo} from 'react';
import { ComboBox, ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';
import styles from './_finance-loan-class-selector.module.scss';
import {FinanceLoanClass, FinanceQuote, FinanceWarranty, hasValue, LookupItem} from "@amsanalytics/react-shared-library";
import {
  getAmsLookupsToDisplay,
  getFinanceLoanClassesToDisplay
} from "../../../Views/OrderEditor/AmsLoanPostingDialog/AmsLoanPostingUtils";

interface WarrantyDisplayItem extends FinanceWarranty {
  displayName: string;
}

export interface FinanceLoanClassSelectorProps {
  financeLoanClasses: FinanceLoanClass[];
  financeQuoteFieldIsDisabled: (fieldName: string) => boolean;
  onLoanClassChange: (loanClass: FinanceLoanClass) => void;
  onChangeSingleField: <K extends keyof FinanceQuote>(fieldName: K, value: FinanceQuote[K], applicationQuoteId?: string) => void;
  financeCompanies: LookupItem[];
  selectedFinanceQuote: FinanceQuote | undefined;
  financeWarranties: FinanceWarranty[];
  onWarrantyChange: (warranty: FinanceWarranty | null) => void;
}

export default function FinanceLoanClassSelector(props: FinanceLoanClassSelectorProps) {
  const financeLoanClassesToDisplay = getFinanceLoanClassesToDisplay(props.financeLoanClasses);
  const selectedFinanceLoanClass = financeLoanClassesToDisplay.find(flc => flc.id === props.selectedFinanceQuote?.financeLoanClassId);
  const fullSelectedLoanClass = props.financeLoanClasses.find(flc => flc.id === props.selectedFinanceQuote?.financeLoanClassId);
  const financeCompaniesToDisplay = getAmsLookupsToDisplay(props.financeCompanies);
  const selectedFinanceCompany = financeCompaniesToDisplay.find(fc => fc.id === props.selectedFinanceQuote?.financeCompanyId);

  const warrantiesToDisplay: WarrantyDisplayItem[] = useMemo(() => {
    if (!fullSelectedLoanClass?.financeWarrantyIds?.length) return [];
    return props.financeWarranties
      .filter(fw => fullSelectedLoanClass.financeWarrantyIds.includes(fw.id) && !fw.deletedDate)
      .map(fw => ({ ...fw, displayName: hasValue(fw.productName) ? `${fw.companyName} - ${fw.productName}` : fw.companyName }));
  }, [props.financeWarranties, fullSelectedLoanClass]);

  const selectedWarranty = warrantiesToDisplay.find(w => w.id === props.selectedFinanceQuote?.financeWarrantyId) ?? null;

  return (
    <div className={styles.financeLoanClassSelectorContainer}>
      <div className={styles.sectionItem}>
        <ComboBox
          disabled={props.financeQuoteFieldIsDisabled("financeLoanClassId")}
          label={'Loan Class'}
          data={financeLoanClassesToDisplay}
          textField="description"
          suggest={true}
          clearButton={false}
          value={selectedFinanceLoanClass}
          onChange={(event: ComboBoxChangeEvent) => {
            const selectedLoanClass = props.financeLoanClasses.find(flc => flc.id === event.value.id);
            if (selectedLoanClass) {
              props.onLoanClassChange(selectedLoanClass);
            }
          }}
        />
      </div>
      <div className={styles.sectionItem}>
        <ComboBox
          disabled={props.financeQuoteFieldIsDisabled("financeCompanyId")}
          label={'Finance Company'}
          data={financeCompaniesToDisplay}
          textField="description"
          suggest={true}
          clearButton={false}
          value={selectedFinanceCompany}
          onChange={(event: ComboBoxChangeEvent) => props.onChangeSingleField("financeCompanyId", event.value?.id ?? "", undefined)}
        />
      </div>
      <div className={styles.sectionItem}>
        <ComboBox
          disabled={!fullSelectedLoanClass || props.financeQuoteFieldIsDisabled("financeWarrantyId")}
          label={'Service Warranty'}
          data={warrantiesToDisplay}
          textField="displayName"
          suggest={true}
          clearButton={true}
          value={selectedWarranty}
          onChange={(event: ComboBoxChangeEvent) => {
            const warranty = event.value
              ? props.financeWarranties.find(fw => fw.id === event.value.id) ?? null
              : null;
            props.onWarrantyChange(warranty);
          }}
        />
      </div>
    </div>
  );
}
